// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Functional core for turning a failed API response body into a user-facing
// message. The server's tier gate (middleware/tier-gate.ts) responds with
// { error: 'upgrade_required', message, requiredTier, currentTier }: `error` is
// a machine token and `message` is the human upgrade prompt. Other endpoints put
// a human string directly in `error`. So prefer `message`, then a non-token
// `error`, then a caller fallback -- never surface the bare 'upgrade_required'
// token to a user. Pure, so the selection rules are unit-testable.

interface ApiErrorBody {
  error?: unknown;
  message?: unknown;
}

// True when the failed response is a tier gate's upgrade-required rejection.
export function isUpgradeRequired(body: unknown): boolean {
  return (
    typeof body === 'object' && body !== null && (body as ApiErrorBody).error === 'upgrade_required'
  );
}

// Best user-facing message for a failed response body, falling back to `fallback`
// when the body carries nothing human (or only the 'upgrade_required' token).
export function apiErrorMessage(body: unknown, fallback: string): string {
  const b = (typeof body === 'object' && body !== null ? body : {}) as ApiErrorBody;
  if (typeof b.message === 'string' && b.message.trim().length > 0) {
    return b.message;
  }
  if (typeof b.error === 'string' && b.error.trim().length > 0 && b.error !== 'upgrade_required') {
    return b.error;
  }
  return fallback;
}
